/**
 * «Карточки в кабинетах» окна товара.
 *
 * Строится из GET /products/:article/presence: зеркала площадок плюс карточки
 * ПИМ — тот же расчёт, что у колонки «Карточки МП» в списке и у вкладки
 * карточек в окне макета. Раньше таблица читала только marketplace_cards и
 * писала «карточек нет» про товар, который уже на витрине.
 */

import React from 'react'
import { ChevronRight, ExternalLink, Loader2 } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { CopyButton } from '../../../components/ui/CopyButton'
import { CARD_STATUS_INFO, PRESENCE_MARKETPLACE_LABEL, PRESENCE_STATE_INFO, formatPrice } from '../lib'
import { presenceTarget, useProductPresence, type PresenceData } from '../presence'
import type { ProductPresenceEntry } from '../types'

const MARKETPLACE_BADGE: Record<string, 'purple' | 'blue' | 'yellow' | 'gray'> = {
  wb: 'purple',
  ozon: 'blue',
  ym: 'yellow',
  site: 'gray',
}

/** Внешний ID: у связанного с витриной — с зеркала, иначе — с карточки ПИМ. */
export function presenceExternalId(entry: ProductPresenceEntry): string | null {
  return entry.mirror?.external_id || entry.pim_card?.external_id || null
}

/** Цена и остаток: карточка ПИМ — то, что мы публикуем; зеркало — запасной источник. */
function shown(entry: ProductPresenceEntry, key: 'price' | 'stock'): number | null {
  return entry.pim_card?.[key] ?? entry.mirror?.[key] ?? null
}

interface ProductPresencePanelProps {
  article: string
  /**
   * Уже загруженное присутствие. Окно товара грузит его один раз и отдаёт
   * цепочке, «Обзору» и «Площадкам»; без этого свойства панель грузит сама.
   */
  data?: PresenceData
  /** Клик по строке: открыть карточку витрины или черновик в окне навигатора. */
  onOpenEntry?: (entry: ProductPresenceEntry) => void
}

export const ProductPresencePanel: React.FC<ProductPresencePanelProps> = ({ article, data, onOpenEntry }) => {
  const own = useProductPresence(data ? null : article)
  const { entries, error } = data ?? own

  if (error) {
    return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
  }
  if (entries === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Загрузка карточек
      </p>
    )
  }
  if (entries.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Карточек в кабинетах нет: товар не связан с витринами и не публиковался конвейером
      </p>
    )
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-2xs uppercase tracking-wider text-gray-500 dark:text-zinc-400">
          <th className="py-1">МП</th>
          <th className="py-1">Кабинет</th>
          <th className="py-1">Состояние</th>
          <th className="py-1 text-right">Цена</th>
          <th className="py-1 text-right">Остаток</th>
          <th className="py-1">Внешний ID</th>
          {onOpenEntry && <th className="py-1" aria-label="Открыть" />}
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          const state = PRESENCE_STATE_INFO[entry.state] ?? PRESENCE_STATE_INFO.none
          const external = presenceExternalId(entry)
          const pimInfo = entry.pim_card ? CARD_STATUS_INFO[entry.pim_card.status] : null
          const detail = [...new Set([...(entry.errors ?? []), entry.pim_card?.error].filter(Boolean))].join('; ') || (entry.state === 'error' ? entry.mirror?.status_label : null)
          const target = onOpenEntry ? presenceTarget(entry, article) : null
          return (
            <tr
              key={`${entry.marketplace}:${entry.seller_id ?? ''}`}
              onClick={target && onOpenEntry ? () => onOpenEntry(entry) : undefined}
              className={
                target
                  ? 'cursor-pointer border-t border-gray-100 align-top hover:bg-gray-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50'
                  : 'border-t border-gray-100 align-top dark:border-zinc-800'
              }
            >
              <td className="py-1.5">
                <Badge variant={MARKETPLACE_BADGE[entry.marketplace] ?? 'gray'}>
                  {PRESENCE_MARKETPLACE_LABEL[entry.marketplace] ?? String(entry.marketplace).toUpperCase()}
                </Badge>
              </td>
              <td className="py-1.5">
                <span className="flex flex-col">
                  <span>{entry.seller_name || (entry.seller_id != null ? `Кабинет ${entry.seller_id}` : '—')}</span>
                  <span className="text-2xs text-gray-500 dark:text-zinc-400">
                    {entry.cabinet_code || entry.seller_short_name || (entry.seller_id != null ? `ID ${entry.seller_id}` : '')}
                  </span>
                </span>
              </td>
              <td className="py-1.5">
                <span className="flex flex-col items-start gap-0.5">
                  <Badge variant={state.variant}>{state.label}</Badge>
                  {entry.mirror && entry.mirror.status_label !== state.label && (
                    <span className="text-2xs text-gray-500 dark:text-zinc-400">{entry.mirror.status_label}</span>
                  )}
                  {!entry.mirror && pimInfo && entry.state !== 'draft' && pimInfo.label !== state.label && (
                    <span className="text-2xs text-gray-500 dark:text-zinc-400">{pimInfo.label}</span>
                  )}
                  {detail && <span className="text-2xs text-red-600 dark:text-red-400">{detail}</span>}
                </span>
              </td>
              <td className="py-1.5 text-right tabular-nums">{formatPrice(shown(entry, 'price'))}</td>
              <td className="py-1.5 text-right tabular-nums">{shown(entry, 'stock') ?? '—'}</td>
              <td className="py-1.5">
                {external ? (
                  <span className="inline-flex items-center gap-1">
                    {entry.mirror?.url ? (
                      <a
                        href={entry.mirror.url}
                        target="_blank"
                        rel="noreferrer"
                        title="Открыть на витрине"
                        className="inline-flex items-center gap-1 font-mono text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {external}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="font-mono text-xs text-gray-700 dark:text-zinc-200">{external}</span>
                    )}
                    <CopyButton value={external} label="внешний ID" size="xs" />
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-zinc-500">—</span>
                )}
              </td>
              {onOpenEntry && (
                <td className="py-1.5 text-right">
                  {target ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenEntry(entry)
                      }}
                      title={entry.mirror ? 'Открыть карточку на витрине' : 'Открыть черновик ПИМ'}
                      className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40"
                    >
                      Открыть
                      <ChevronRight className="h-3 w-3" aria-hidden="true" />
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-zinc-500">нет карточки</span>
                  )}
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
